import {Link} from 'react-router-dom';
import {ArrowLeft, ArrowUpRight, FileText} from 'lucide-react';
import {StaticPage} from '@client/pages/public/enroll/components/StaticPage';
import {HighlightAndDisclosures} from '@client/pages/public/enroll/components/HighlightAndDisclosures';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import type {ResourceDoc} from '@client/pages/public/enroll/types';
import {isPdfHref} from '@common/enrollmentSite/siteLinks';
import {useLinkHref} from '@client/pages/public/enroll/enrollPreview';

export function EnrollResources() {
  const group = useEnrollGroup();
  const base = `/${group.slug}`;
  const content = group.resourcesHidden ? undefined : group.resourcesPage;

  if (!content) {
    return (
      <StaticPage eyebrow={group.name} title='Plan resources'>
        Plan documents for this group aren't available online yet — contact your{' '}
        {group.benefitsContact ?? 'HR office'} or the Concierge Team at {group.supportPhone} for
        benefit details and required notices.
      </StaticPage>
    );
  }

  return (
    <div className={`mx-auto max-w-7xl space-y-8 ${POSTHOG_UNMASK_CLASS}`}>
      <Link
        to={base}
        className='inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-[var(--enroll-primary)]'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to home
      </Link>

      <header className='space-y-3'>
        <h1 className='text-4xl font-bold tracking-tight text-[var(--enroll-ink)]'>
          {content.title || 'Plan Resources'}
        </h1>
        <p className='text-neutral-500'>{content.intro}</p>
      </header>

      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {content.documents.map((doc, i) => (
          <DocCard key={i} doc={doc} />
        ))}
      </div>

      <HighlightAndDisclosures highlight={content.virtualCare} disclosures={content.disclosures} />
    </div>
  );
}

export function DocCard({doc}: {doc: ResourceDoc}) {
  const linkHref = useLinkHref();
  return (
    <a
      href={linkHref(doc.href)}
      target='_blank'
      rel='noreferrer'
      className='group flex flex-col rounded-2xl border border-neutral-200 bg-white p-5 transition-colors hover:border-[var(--enroll-accent)]'
    >
      <div className='flex items-center gap-3'>
        <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-orange-100 text-[var(--enroll-accent)]'>
          <FileText className='h-5 w-5' />
        </span>
        <h2 className='text-sm font-semibold text-[var(--enroll-ink)]'>{doc.title}</h2>
      </div>
      <p className='mt-3 flex-1 text-sm leading-relaxed text-neutral-500'>{doc.description}</p>
      <span className='mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--enroll-accent)] group-hover:underline'>
        {isPdfHref(doc.href) ? 'Open PDF' : 'Open link'}
        <ArrowUpRight className='h-3.5 w-3.5' />
      </span>
    </a>
  );
}
